import {
  Flex,
  SidebarMenuItem,
  SidebarDivider,
  SidebarMenuTitle,
  flattenErrorTypeArray,
  FlattenedErrorsObjectArray,
  Errors,
  ScrollSidebarTitleItem,
  CheckBolderIcon,
  useRefs,
  PageRefName,
  SidebarMenuItemDynamic,
  useIsVisible,
  LockIcon,
} from '@equifinance/shared';
import styled from 'styled-components';
import { useState } from 'react';
import ProductSidebarHeader from '../Products/ProductSidebar/ProductSidebarHeader';
import ProductSidebarSummary from '../Products/ProductSidebar/ProductSidebarSummary';
import {
  CreditAddress, addressTypeFromAddress,
  creditAddressSort,
  useCreditContext,
} from '../../contexts/credit';
import { SectionTitleIcon } from '../Account/Icons/SectionTitleIcon';
import {
  IconCircleSmall,
} from '../Sidebar';
import { usePageConfigurationContext } from '../../contexts/pageConfiguration';
import { DisbursementSummaryRefName } from './DisbursementDetails/DisbursementDetails';
import { useTabNavHeight } from '../AccountTabNav';
import calculateOffsetTop from '../../utils/calculateOffsetTop';
import CrossIcon from '../Account/Icons/CrossIcon';

const MenuTitleCollapsibleIcon = styled.i` ${({ theme }) => `
  font-size: ${theme.fontSize[3]};
  margin-left: auto;
  cursor: pointer;
`}`;

const SidebarMenuAddress = styled(Flex)` ${({ theme }) => `
    align-items:center;
    gap:${theme.padding[2]};
    padding-left: ${theme.padding[2]};
`}`;

const SidebarMenuContainer = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[2]};
  align-items: center;
`}`;

const CreditSidebarMenuItemDynamic = styled(SidebarMenuItemDynamic)`${({ theme }) => `
  li {
    border-left: none !important;

    :active {
      border-left: none !important;
    }
  }

  li:not(:first-of-type) {
    > a:active {
      background: ${theme.colours.secondary[7]};

      div {
        font-weight: ${theme.fontWeight.bold};
      }
    }
  }

  :hover {
    background: none;
  }
`}`;

function SidebarCreditMenu({
  creditType,
  addresses = {},
  errors,
  refName,
  onClickAddress,
}: {
  creditType: string,
  addresses: Record<string, CreditAddress>,
  errors: FlattenedErrorsObjectArray
  refName: string,
  onClickAddress: (addressId: string) => void
}) {
  const { refs } = useRefs();
  const [open, setOpen] = useState<boolean>(true);
  const stickyHeaderOffset = useTabNavHeight();

  const onClickMenuTitle = () => {
    setOpen((prev) => !prev);
  };

  const { isReadOnly } = usePageConfigurationContext();

  const isOnScreen = useIsVisible(refs[refName], stickyHeaderOffset);

  return (
    <CreditSidebarMenuItemDynamic hasSubSection={false} highlight={isOnScreen}>
      <SidebarMenuItem onClick={onClickMenuTitle}>
        <SidebarMenuContainer>
          <SectionTitleIcon
            isLocked={isReadOnly}
            errors={errors}
            tickIcon={(
              <IconCircleSmall>
                <CheckBolderIcon />
              </IconCircleSmall>
            )}
            crossIcon={(
              <CrossIcon size={17} />
            )}
            lockIcon={<LockIcon small />}
          />
          <SidebarMenuTitle>{creditType}</SidebarMenuTitle>
          <MenuTitleCollapsibleIcon className={open ? 'mi-chevron-up' : 'mi-chevron-down'} />
        </SidebarMenuContainer>
      </SidebarMenuItem>

      {open && (
        <>
          {Object.values(addresses).sort(creditAddressSort).map((address) => {
            const addressType = addressTypeFromAddress(address);

            return (
              <SidebarMenuItem key={address.id} onClick={() => onClickAddress(address.id)}>
                <SidebarMenuAddress>
                  {`${addressType.charAt(0).toUpperCase() + addressType.slice(1)} address`}
                </SidebarMenuAddress>
              </SidebarMenuItem>
            );
          })}
        </>
      )}

      <SidebarDivider />
    </CreditSidebarMenuItemDynamic>
  );
}

const getCreditErrors = <T extends { id: string }>(
  errors: Errors,
  item: T,
  errorKey: keyof T,
) => flattenErrorTypeArray([
    ...(errors[item.id] ?? []),
    ...(Object.keys(item[errorKey] ?? {}).map((id) => errors[id] ?? []).flat()),
  ]);

export default function CreditSidebar() {
  const {
    credit,
    securedCreditAddresses,
    unsecuredCreditAddresses,
    insolvencyAddresses,
  } = useCreditContext();

  const stickyHeaderOffset = useTabNavHeight();
  const { refs } = useRefs();

  const onClickSidebarAddress = (id: string, type: string) => {
    const rn = `${type}-credit-address-${id}`;
    const { current } = refs?.[rn] ?? {};
    const offsetTop = calculateOffsetTop(current);

    if (current) {
      refs[PageRefName]?.current.scrollTo({ top: offsetTop - stickyHeaderOffset, behavior: 'smooth' });
    }
  };

  const securedCreditErrors = getCreditErrors(credit.errors, credit.securedCreditItems, 'creditItems');
  const unsecuredCreditErrors = getCreditErrors(credit.errors, credit.unsecuredCreditItems, 'creditItems');
  const insolvencyErrors = getCreditErrors(credit.errors, credit.insolvencyRecords, 'insolvencyRecords');

  return (
    <>
      <ProductSidebarHeader />

      <SidebarDivider />

      <SidebarCreditMenu
        creditType="Secured Credit"
        addresses={securedCreditAddresses}
        errors={securedCreditErrors}
        refName="secured-credit-table"
        onClickAddress={(addressId: string) => onClickSidebarAddress(addressId, 'secured')}
      />
      <SidebarCreditMenu
        creditType="Unsecured Credit"
        addresses={unsecuredCreditAddresses}
        errors={unsecuredCreditErrors}
        refName="unsecured-credit-table"
        onClickAddress={(addressId: string) => onClickSidebarAddress(addressId, 'unsecured')}
      />
      <SidebarCreditMenu
        creditType="Court and Insolvency"
        addresses={insolvencyAddresses}
        errors={insolvencyErrors}
        refName="insolvency-credit-table"
        onClickAddress={(addressId: string) => onClickSidebarAddress(addressId, 'insolvency')}
      />
      <ScrollSidebarTitleItem
        stickyHeaderOffset={stickyHeaderOffset}
        key="products-sidebar-item"
        refName={DisbursementSummaryRefName}
      >
        Summary of Disbursement
      </ScrollSidebarTitleItem>

      <ProductSidebarSummary />
    </>
  );
}
